import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { ReceiptText, Loader2, Truck, BadgeEuro, Utensils, Wine, Clock, Printer, AlertTriangle } from "lucide-react";
import { printReceipt } from "./receipt";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PREP = {
  new: { label: "New", cls: "bg-amber-400 text-amber-950" },
  preparing: { label: "Preparing", cls: "bg-sea text-white" },
  ready: { label: "Ready to run", cls: "bg-emerald-500 text-white" },
  served: { label: "Served", cls: "bg-sage/20 text-sage" },
};

const ago = (iso) => {
  if (!iso) return "";
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 1 ? "just now" : `${m}m ago`;
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "undelivered", label: "To deliver" },
  { id: "uninvoiced", label: "To POS" },
  { id: "done", label: "Closed" },
];

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const today = format(new Date(), "yyyy-MM-dd");

  const load = () => axios.get(`${API}/orders`, { params: { date: today } })
    .then((r) => { setOrders(r.data); setLoading(false); }).catch(() => setLoading(false));

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const setFlagGroup = async (group, field) => {
    const next = !group.every((o) => o[field]);
    const ids = new Set(group.map((o) => o.id));
    setOrders((prev) => prev.map((x) => (ids.has(x.id) ? { ...x, [field]: next } : x)));
    try { await Promise.all(group.map((o) => axios.patch(`${API}/orders/${o.id}/flags`, { [field]: next }))); }
    catch { toast.error("Could not update"); load(); }
  };
  const setFlagOne = async (o, field) => {
    const next = !o[field];
    setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, [field]: next } : x)));
    try { await axios.patch(`${API}/orders/${o.id}/flags`, { [field]: next }); }
    catch { toast.error("Could not update"); load(); }
  };

  const groups = useMemo(() => {
    const map = {};
    orders.forEach((o) => { const k = o.ticket || o.id; (map[k] = map[k] || []).push(o); });
    const arr = Object.values(map).filter((g) => {
      const anyUndel = g.some((o) => !o.delivered);
      const anyUninv = g.some((o) => !o.invoiced);
      if (filter === "undelivered") return anyUndel;
      if (filter === "uninvoiced") return anyUninv;
      if (filter === "done") return !anyUndel && !anyUninv;
      return true;
    });
    const gTime = (g) => Math.max(...g.map((o) => new Date(o.created_at).getTime()));
    arr.sort((a, b) => gTime(b) - gTime(a));
    return arr;
  }, [orders, filter]);

  const openCount = orders.filter((o) => !o.invoiced).length;
  const openTotal = orders.filter((o) => !o.invoiced).reduce((s, o) => s + o.total, 0);

  return (
    <div className="mx-auto max-w-4xl p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-display text-2xl tracking-wide text-slate-900"><ReceiptText size={22} className="text-brand-700" /> Orders</h2>
        <div className="rounded-full bg-white px-3 py-1.5 text-sm ring-1 ring-sand-300" data-testid="orders-open-summary">
          <span className="font-semibold text-slate-700">{openCount}</span> <span className="text-slate-400">unbilled ·</span> <span className="font-bold text-brand-700">{openTotal.toFixed(2)}€</span>
        </div>
      </div>

      <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={`orders-filter-${f.id}`} onClick={() => setFilter(f.id)}
            className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${filter === f.id ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-sand-300"}`}>{f.label}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-20 text-slate-400"><Loader2 className="animate-spin" /></div> : (
        groups.length === 0 ? <p className="rounded-xl bg-white/60 py-12 text-center text-sm text-slate-400">No orders here</p> : (
          <div className="space-y-2.5" data-testid="orders-list">
            {groups.map((g) => {
              const first = g[0];
              const key = first.ticket || first.id;
              const allItems = g.flatMap((o) => o.items.map((it) => ({ ...it, _st: o.station })));
              const total = g.reduce((s, o) => s + o.total, 0);
              const allInvoiced = g.every((o) => o.invoiced);
              const allDelivered = g.every((o) => o.delivered);
              const allergies = g.find((o) => o.allergies)?.allergies;
              const note = g.find((o) => o.note)?.note;
              const pay = g.find((o) => o.payment_method);
              return (
                <div key={key} data-testid={`orders-row-${key}`} className={`rounded-2xl border bg-white p-3.5 ${allInvoiced ? "border-sand-200 opacity-70" : "border-sand-300"}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-lg text-slate-900">{first.spot_label}</span>
                    {g.map((o) => {
                      const prep = PREP[o.status] || PREP.new;
                      return (
                        <span key={o.id} className="flex items-center gap-1">
                          <span className={`flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase ${o.station === "kitchen" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sea"}`}>
                            {o.station === "kitchen" ? <><Utensils size={11} /> Food</> : <><Wine size={11} /> Drinks</>}
                          </span>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${prep.cls}`}>{prep.label}</span>
                        </span>
                      );
                    })}
                    <span className="ml-auto flex items-center gap-1 text-[11px] text-slate-400"><Clock size={11} /> {ago(first.created_at)}</span>
                  </div>
                  <ul className="mt-1.5 space-y-0.5 text-sm text-slate-600" data-testid={`orders-items-${key}`}>
                    {allItems.map((i, idx) => (
                      <li key={`${i.name}-${idx}`} className="flex items-baseline gap-1.5">
                        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${i._st === "kitchen" ? "bg-amber-500" : "bg-sea"}`} />
                        <span>{i.qty}× {i.name}
                          {i.options?.length ? <span className="text-slate-400"> · {i.options.join(" · ")}</span> : null}
                          {i.note ? <span className="italic text-slate-400"> — {i.note}</span> : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {allergies && <p className="mt-1 flex items-center gap-1 text-xs font-bold text-destructive"><AlertTriangle size={12} /> Allergies: {allergies}</p>}
                  <div className="mt-1.5 flex items-center justify-between">
                    {pay ? <span data-testid={`orders-pay-${key}`} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600">{pay.payment_method}{pay.payment_ref ? ` · ${pay.payment_ref}` : ""}</span> : <span />}
                    <div className="flex items-center gap-2">
                      <button data-testid={`orders-print-${key}`} onClick={() => printReceipt(first.spot_label, g)} title="Print receipt (this device)" className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-500 hover:bg-sand-100"><Printer size={14} /></button>
                      <button data-testid={`orders-sendprinter-${key}`} onClick={() => axios.post(`${API}/print/receipt/${first.ticket || first.id}`).then(() => toast.success("Sent to printer")).catch(() => toast.error("Could not queue print"))} title="Send receipt to Star printer" className="flex h-7 w-7 items-center justify-center rounded-lg text-brand-700 hover:bg-brand/10"><ReceiptText size={14} /></button>
                      <span className="font-bold text-slate-800">{total.toFixed(2)}€</span>
                    </div>
                  </div>
                  {note && <p className="mt-1 text-xs italic text-amber-700">“{note}”</p>}
                  <div className="mt-2.5 flex gap-2">
                    <button data-testid={`orders-invoiced-${key}`} onClick={() => setFlagGroup(g, "invoiced")}
                      className={`flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg text-sm font-semibold transition-colors ${allInvoiced ? "bg-brand text-white" : "bg-white text-slate-600 ring-1 ring-sand-300 hover:bg-sand-100"}`}>
                      <BadgeEuro size={15} /> {allInvoiced ? "In POS" : "Add to POS"}
                    </button>
                    {g.length === 1 ? (
                      <button data-testid={`orders-delivered-${key}`} onClick={() => setFlagOne(g[0], "delivered")}
                        className={`flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg text-sm font-semibold transition-colors ${g[0].delivered ? "bg-sage text-white" : "bg-white text-slate-600 ring-1 ring-sand-300 hover:bg-sand-100"}`}>
                        <Truck size={15} /> {g[0].delivered ? "Delivered" : "Mark delivered"}
                      </button>
                    ) : (
                      g.map((o) => (
                        <button key={o.id} data-testid={`orders-delivered-${o.id}`} onClick={() => setFlagOne(o, "delivered")}
                          className={`flex h-9 flex-1 items-center justify-center gap-1 rounded-lg text-xs font-semibold transition-colors ${o.delivered ? "bg-sage text-white" : "bg-white text-slate-600 ring-1 ring-sand-300 hover:bg-sand-100"}`}>
                          <Truck size={13} /> {o.delivered ? (o.station === "kitchen" ? "Food done" : "Drinks done") : (o.station === "kitchen" ? "Deliver food" : "Deliver drinks")}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
